import React from 'react';
import {
	AbsoluteFill,
	interpolate,
	Sequence,
	useCurrentFrame,
	useVideoConfig,
	spring,
	Img,
	OffthreadVideo,
	Easing,
} from 'remotion';

import { z } from 'zod';
import { zColor } from '@remotion/zod-types';

import { CroppedImg, CropRect } from './CroppedImg';

export interface SocialImage {
	url: string;
	crop_start?: CropRect;
	crop_end?: CropRect;
	image_id: string;
	original_width?: number;
	original_height?: number;
}



import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { fade } from "@remotion/transitions/fade";
import { wipe } from "@remotion/transitions/wipe";
import { flip } from "@remotion/transitions/flip";
import { clockWipe } from "@remotion/transitions/clock-wipe";
import { iris } from "@remotion/transitions/iris";
import { cube } from "../extras/cube";


type TransitionType = 'slide' | 'fade' | 'wipe' | 'flip' | 'clockWipe' | 'iris' | 'cube';

export const ImageCarousel: React.FC<{
	primaryColor: string;
	secondaryColor: string;
	/** Duration in frames (integer) */
	durationInFrames: number;

	socialImages: SocialImage[];
	transitionType: TransitionType;
}> = ({ primaryColor, secondaryColor, durationInFrames, socialImages, transitionType }) => {
	const { width, height, fps } = useVideoConfig(); // unused but safe to keep
	const transitionDuration = Math.ceil(0.5 * fps); // 0.5 seconds

	// Each image shows for equal duration over total frames
	const framesPerImage = Math.floor(durationInFrames / socialImages.length);

	const getTransitionPresentation = (type: TransitionType) => {
		switch (type) {
			case 'slide':
				return slide();
			case 'fade':
				return fade();
			case 'wipe':
				return wipe();
			case 'flip':
				return flip();
			case 'clockWipe':
				return clockWipe({ width, height });
			case 'iris':
				return iris({ width, height });
			case 'cube':
				return cube({ direction: "from-right", perspective: 1000 });
			default:
				return slide();
		}
	};

	// Component for each image with its own zoom effect
	const ImageWithZoom: React.FC<{ image: SocialImage }> = ({ image }) => {
		const frame = useCurrentFrame();

		// Zoom timing - start at 30% and complete by the end of the sequence
		// Note from instruction: zoom effect is now handled by crop_start/end if available.
		// However, we still need progress 0..1

		const zoomUntilFrame = framesPerImage + transitionDuration;

		const progress = interpolate(frame, [0, zoomUntilFrame], [0, 1], {
			extrapolateLeft: 'clamp',
			extrapolateRight: 'clamp',
			easing: Easing.linear,
		});
		// Using linear for crop interpolation is standard for Ken Burns, but quad is fine too. 
		// Previous zoom used quad. Let's use linear for crop movement to be precise.

		// Note: If no crop info, we might want fallback behavior.
		// The CroppedImg handles missing cropStart by just fitting cover.
		// But do we want the "old" zoom effect for images without crop data?
		// For now, let's just pass data to CroppedImg.

		// If NO crop data, maybe apply the old zoom effect?
		// The prompt says "change ImageWithZoom so that it uses the crop_start information".
		// It implies replacing the logic.

		const oldZoom = interpolate(frame, [Math.floor(framesPerImage * 0.3), zoomUntilFrame], [1, 1.1], {
			extrapolateLeft: 'clamp',
			extrapolateRight: 'clamp',
			easing: Easing.out(Easing.quad),
		});

		if (!image.crop_start) {
			// Legacy behavior for non-cropped images
			return (
				<AbsoluteFill style={{ transform: `scale(${oldZoom})` }}>
					{image.url.endsWith('.mp4') ? (
						<OffthreadVideo
							src={image.url}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
							}}
						/>
					) : (
						<Img
							src={image.url}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
							}}
						/>
					)}
				</AbsoluteFill>
			);
		}

		return (
			<AbsoluteFill>
				<CroppedImg
					src={image.url}
					cropStart={image.crop_start}
					cropEnd={image.crop_end}
					progress={progress}
					imageWidth={image.original_width}
					imageHeight={image.original_height}
				/>
			</AbsoluteFill>
		);
	};

	return (
		<TransitionSeries>
			{socialImages.map((image, index) => (
				<React.Fragment key={index}>
					<TransitionSeries.Sequence durationInFrames={framesPerImage + transitionDuration}>
						<ImageWithZoom image={image} />
					</TransitionSeries.Sequence>
					{index < socialImages.length - 1 && (
						<TransitionSeries.Transition
							presentation={getTransitionPresentation(transitionType)}
							timing={linearTiming({ durationInFrames: transitionDuration })}
						/>
					)}
				</React.Fragment>
			))}
		</TransitionSeries>
	);
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
